<template>
  <div :style="wrapperStyle" class="form-multiselect" role="group" :aria-labelledby="fieldLabel ? labelId : undefined">
    <div v-if="fieldLabel" :id="labelId" class="form-field-label">{{ fieldLabel }}</div>
    <input type="hidden" :name="uniqueTitle" :value="storedValue" />
    <div v-if="displayStyle === 'checkboxes' || (displayStyle === 'radio' && allowsMultiple)" class="form-multiselect-choices">
      <label v-for="choice in normalizedChoices" :key="choice" class="form-multiselect-choice">
        <input
          type="checkbox"
          :checked="isSelected(choice)"
          :disabled="isReadonly"
          @change="onCheck(choice, $event.target.checked)"
        />
        <span>{{ choice }}</span>
      </label>
    </div>
    <div v-else-if="displayStyle === 'radio'" class="form-multiselect-choices">
      <label v-for="choice in normalizedChoices" :key="choice" class="form-multiselect-choice">
        <input
          type="radio"
          :name="uniqueTitle"
          :value="choice"
          :checked="isSelected(choice)"
          :disabled="isReadonly"
          @change="onRadio(choice)"
        />
        <span>{{ choice }}</span>
      </label>
    </div>
    <select
      v-else-if="displayStyle === 'list'"
      :id="uniqueTitle"
      class="form-select form-multiselect-list"
      :multiple="allowsMultiple"
      :size="listSize"
      :disabled="isReadonly"
      :aria-describedby="describedBy"
      :value="selectModel"
      @change="onSelect"
    >
      <option v-for="choice in normalizedChoices" :key="choice" :value="choice">{{ choice }}</option>
    </select>
    <select
      v-else
      :id="uniqueTitle"
      class="form-select"
      :multiple="allowsMultiple"
      :disabled="isReadonly"
      :aria-describedby="describedBy"
      :value="selectModel"
      @change="onSelect"
    >
      <option v-if="!allowsMultiple" value="">{{ placeholder || 'select...' }}</option>
      <option v-for="choice in normalizedChoices" :key="choice" :value="choice">{{ choice }}</option>
    </select>
    <div v-if="!normalizedChoices.length" class="form-multiselect-empty">No choices defined</div>
  </div>
</template>

<script>
import { inject, computed } from 'vue'
import { formattingToStyle } from '../../lib/client-rule-evaluator'

function normalizeChoices (choices) {
  if (!Array.isArray(choices)) return []
  const seen = new Set()
  const normalized = []
  for (const choice of choices) {
    const value = String(choice == null ? '' : choice).trim()
    if (!value || seen.has(value)) continue
    seen.add(value)
    normalized.push(value)
  }
  return normalized
}

function parseValueList (raw, choices) {
  if (raw == null || raw === '') return []
  if (Array.isArray(raw)) return raw.flatMap(item => parseValueList(item, choices))
  if (typeof raw !== 'string') return [String(raw)]
  const trimmed = raw.trim()
  if (!trimmed) return []
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.flatMap(item => parseValueList(item, choices))
    } catch (error) {}
  }
  const allowed = normalizeChoices(choices)
  if (allowed.includes(trimmed)) return [trimmed]
  if (trimmed.includes(',')) return trimmed.split(',').map(part => part.trim()).filter(Boolean)
  return [trimmed]
}

export default {
  props: {
    placeholder: { type: String, default: '' },
    uniqueTitle: { type: String, default: '' },
    fieldLabel: { type: String, default: '' },
    description: { type: String, default: '' },
    choices: { type: Array, default: () => [] },
    displayStyle: { type: String, default: 'dropdown' },
    selectionMode: { type: String, default: 'multiple' }
  },
  setup (props) {
    const formFormatting = inject('formFormatting', null)
    const formReadonly = inject('formReadonly', null)
    const formValues = inject('formValues', null)
    const updateFormValue = inject('updateFormValue', null)

    const wrapperStyle = computed(() => {
      if (!formFormatting || !props.uniqueTitle) return {}
      const fmt = formFormatting[props.uniqueTitle]
      return fmt ? formattingToStyle(fmt) : {}
    })

    const isReadonly = computed(() => {
      if (!formReadonly || !props.uniqueTitle) return false
      return !!formReadonly[props.uniqueTitle]
    })

    const labelId = computed(() => `${props.uniqueTitle}-label`)

    const describedBy = computed(() => {
      return props.description ? `${props.uniqueTitle}-description` : undefined
    })

    return { formFormatting, formReadonly, formValues, updateFormValue, wrapperStyle, isReadonly, labelId, describedBy }
  },
  data () {
    return {
      selected: []
    }
  },
  computed: {
    allowsMultiple () {
      return this.selectionMode !== 'single'
    },
    normalizedChoices () {
      return normalizeChoices(this.choices)
    },
    listSize () {
      return Math.min(Math.max(this.normalizedChoices.length, 2), 8)
    },
    selectModel () {
      if (this.allowsMultiple) return this.selected
      return this.selected[0] || ''
    },
    storedValue () {
      if (!this.allowsMultiple) return this.selected[0] || ''
      return JSON.stringify(this.selected)
    }
  },
  watch: {
    formValues: {
      deep: true,
      handler () {
        this.syncFromForm()
      }
    },
    choices () {
      this.syncFromForm()
    },
    selectionMode () {
      this.publish(this.selected)
    }
  },
  mounted () {
    this.syncFromForm()
  },
  methods: {
    isSelected (choice) {
      return this.selected.includes(choice)
    },
    syncFromForm () {
      if (!this.formValues || !this.uniqueTitle) return
      const next = this.limit(parseValueList(this.formValues[this.uniqueTitle], this.normalizedChoices))
      if (JSON.stringify(next) === JSON.stringify(this.selected)) return
      this.selected = next
    },
    limit (values) {
      const allowed = new Set(this.normalizedChoices)
      const filtered = this.normalizedChoices.length
        ? values.filter(value => allowed.has(value))
        : values
      const seen = new Set()
      const unique = filtered.filter(value => {
        if (seen.has(value)) return false
        seen.add(value)
        return true
      })
      if (!this.allowsMultiple) return unique.slice(0, 1)
      return unique
    },
    publish (values) {
      this.selected = this.limit(values)
      if (this.updateFormValue) {
        this.updateFormValue(this.uniqueTitle, this.allowsMultiple ? this.selected.slice() : (this.selected[0] || ''))
      }
    },
    onCheck (choice, checked) {
      if (this.isReadonly) return
      if (!this.allowsMultiple) {
        this.publish(checked ? [choice] : [])
        return
      }
      const next = this.selected.filter(value => value !== choice)
      if (checked) next.push(choice)
      this.publish(next)
    },
    onRadio (choice) {
      if (this.isReadonly) return
      if (!this.allowsMultiple) {
        this.publish([choice])
        return
      }
      if (this.selected.includes(choice)) {
        this.publish(this.selected.filter(value => value !== choice))
        return
      }
      this.publish(this.selected.concat(choice))
    },
    onSelect (event) {
      if (this.isReadonly) return
      const options = Array.from(event.target.selectedOptions || [])
      this.publish(options.map(option => option.value).filter(Boolean))
    }
  }
}
</script>

<style scoped>
.form-multiselect-choices {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-multiselect-choice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}
.form-multiselect-list {
  min-width: 12rem;
}
.form-multiselect-empty {
  color: #888;
  font-size: 0.85rem;
  margin-top: 4px;
}
</style>
